'use client';

import { useEffect, useRef } from 'react';

/**
 * Panel back-stack — makes the browser Back button / gesture pop the workspace's
 * open panel + drill-down states (LIFO) BEFORE navigating away from the page.
 *
 * Each dismissable UI state (the left panel being open, a channel/app detail
 * drill-down) registers itself via `usePanelBack(active, onClose)`. Opening
 * pushes a history entry; a browser Back pops the top entry and calls its
 * `onClose` instead of leaving the workspace. Closing via UI pops the matching
 * history entry too (so the stacks stay in sync). Only when the stack is empty
 * does Back navigate normally.
 */

type Entry = { close: () => void };

const stack: Entry[] = [];
let ignoreNextPop = false;
let installed = false;

function ensureListener() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('popstate', () => {
    // A UI-driven close already accounted for its history entry.
    if (ignoreNextPop) {
      ignoreNextPop = false;
      return;
    }
    const entry = stack.pop();
    if (entry) {
      try { entry.close(); } catch { /* the state may already be gone */ }
    }
    // Empty stack → let the browser navigate (do nothing).
  });
}

/** Register a dismissable state; returns a disposer for the UI-close path.
 * `href` (optional) stamps the pushed entry with a REAL public URL — the
 * Discover stack uses it so an open channel/post detail is linkable and
 * reload lands on the canonical page. Next App Router treats userland
 * pushState as shallow (no route change, no refetch); Back restores the
 * previous entry's URL for free because each level is its own entry. */
function pushPanelBack(close: () => void, href?: string | null): () => void {
  ensureListener();
  const entry: Entry = { close };
  stack.push(entry);
  try {
    window.history.pushState({ __panelBack: true }, '', href ?? undefined);
  } catch { /* history unavailable — degrade to no trap */ }
  return () => {
    const idx = stack.indexOf(entry);
    if (idx < 0) return; // already consumed by a Back press — nothing to undo
    stack.splice(idx, 1);
    // UI closed it: rewind the history entry we pushed — but ONLY if our
    // sentinel is still the current history top. If the component unmounted
    // because the user navigated away, the router has already replaced
    // history.state, so rewinding here would fight the navigation.
    const top = (window.history.state as { __panelBack?: boolean } | null) ?? null;
    if (top?.__panelBack) {
      ignoreNextPop = true;
      try {
        window.history.back();
      } catch {
        ignoreNextPop = false;
      }
    }
  };
}

/**
 * While `active`, the browser Back button/gesture closes this panel state
 * (calls `onClose`) instead of navigating the workspace. Nested states pop
 * LIFO (a drill-down closes before the panel that contains it).
 */
export function usePanelBack(active: boolean, onClose: () => void): void {
  const cb = useRef(onClose);
  cb.current = onClose;
  useEffect(() => {
    if (!active) return;
    return pushPanelBack(() => cb.current());
  }, [active]);
}

/**
 * Like `usePanelBack`, but for a multi-level drill-down: keeps one history
 * entry per level so a browser Back / mouse-back pops exactly ONE level (calls
 * `onPop`) instead of collapsing the whole stack. Growing `depth` pushes
 * entries; a UI-driven pop (lower `depth`) rewinds the extra ones. Used by the
 * Feed channel drill-down (`//apps` → `//apps/collaboration` →
 * `//apps/collaboration/collab-board`).
 */
export function usePanelBackStack(
  depth: number,
  onPop: () => void,
  /** Public URL for the entry being pushed (linkability). Only consulted when
   * `depth` grows; levels without a public path pass nothing. */
  hrefForTop?: string | null,
): void {
  const cb = useRef(onPop);
  cb.current = onPop;
  const hrefRef = useRef(hrefForTop);
  hrefRef.current = hrefForTop;
  // One disposer per pushed history entry, so the count mirrors `depth`.
  const disposers = useRef<Array<() => void>>([]);
  useEffect(() => {
    const current = disposers.current.length;
    if (depth > current) {
      // Drilled deeper — push a Back-trapping entry for each new level. Only
      // the TOP entry gets the href (a multi-level jump has one visible URL).
      for (let i = current; i < depth; i++) {
        disposers.current.push(
          pushPanelBack(() => cb.current(), i === depth - 1 ? hrefRef.current : null),
        );
      }
    } else if (depth < current) {
      // Popped (Back button or a browser Back that already consumed the top
      // entry) — release the surplus. A disposer whose entry a browser Back
      // already consumed is a no-op; a UI pop's disposer rewinds its history
      // entry. Both keep the count in sync with `depth`.
      for (let i = current; i > depth; i--) {
        disposers.current.pop()?.();
      }
    }
  }, [depth]);
  // On unmount, release every remaining entry.
  useEffect(
    () => () => {
      while (disposers.current.length) disposers.current.pop()?.();
    },
    [],
  );
}
